import clsx, { type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type React from "react"
import type { SxProps } from "../types"

export const camelToKebab = (str: string): string =>
  str.replace(/([A-Z])/g, '-$1').toLowerCase()

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs))
}

/**
 * Convert sx prop to React CSSProperties object
 * Similar to MUI's sx system but simplified for CSS-in-JS
 */
export function sxToCSS(sx?: SxProps): React.CSSProperties {
  if (!sx) return {}

  const processStyleObject = (obj: any): React.CSSProperties => {
    const result: React.CSSProperties = {}

    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'function') {
        // Handle function values (for theme support in the future)
        const processedValue = value()
        if (typeof processedValue === 'object' && processedValue !== null) {
          Object.assign(result, processStyleObject(processedValue))
        } else {
          // Type assertion for CSS property values
          const cssKey = key as keyof React.CSSProperties
            ; (result as any)[cssKey] = processedValue
        }
      } else if (typeof value === 'object' && value !== null) {
        // Skip nested selectors for inline styles - they'll be handled by sxToStyleTag
        continue
      } else {
        // Handle direct CSS property values
        // Type assertion for CSS property values
        const cssKey = key as keyof React.CSSProperties
          ; (result as any)[cssKey] = value
      }
    }

    return result
  }

  return processStyleObject(sx)
}

/**
 * Generate CSS string from sx object for nested selectors
 * Returns CSS text that can be inserted into a style tag
 */
export function sxToStyleTag(sx?: SxProps, containerClass?: string, opts: { important?: boolean } = {}): string {
  if (!sx) return ''

  const containerSelector = containerClass ? `.${containerClass}` : ':root'

  // Common unitless CSS properties. Others require units when numbers are provided.
  const UNITLESS = new Set([
    'line-height',
    'z-index',
    'opacity',
    'flex',
    'flex-grow',
    'flex-shrink',
    'font-weight',
    'order',
    'zoom'
  ])
  /**
   * Processes a CSS value and handles !important correctly
   */
  const processCSSValue = (property: string, value: any): string => {
    const strValue = String(value).trim()

    // Check if user already included !important
    if (strValue.endsWith('!important')) {
      return `${property}: ${strValue};`
    }

    // Validate common CSS properties and values
    const isValidCSSValue = (prop: string, val: string): boolean => {
      // Basic validation - can be expanded
      if (val.includes(';') || val.includes('{') || val.includes('}')) {
        console.warn(`Invalid CSS value for property "${prop}": ${val}`)
        return false
      }
      return true
    }

    if (!isValidCSSValue(property, strValue)) {
      return '' // Skip invalid values
    }

    // Add !important by default for specificity
    let finalVal = strValue
    if (typeof value === 'number' && !UNITLESS.has(property)) {
      finalVal = `${value}px`
    }
    return `${property}: ${finalVal}${opts.important ? ' !important' : ''};`
  }

  const processNestedStyles = (obj: any, parentSelector: string = '') => {
    let css = ''
    let directStyles = ''

    for (const [key, value] of Object.entries(obj)) {
      const v = typeof value === 'function' ? value() : value
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
        // Handle nested selectors
        if (key.startsWith('&')) {
          // Handle & selectors (pseudo-classes, etc.)
          const base = parentSelector || containerSelector || ':where(:root)'
          const selector = key.replace('&', base)
          css += processNestedStyles(v, selector)
        } else if (key.startsWith('.') || key.startsWith('#') || key.includes(' ') || key.startsWith('@')) {
          // Handle class, id, descendant selectors, and media queries
          if (key.startsWith('@')) {
            // Media queries and other @ rules
            css += `${key} { ${processNestedStyles(v, parentSelector)} }`
          } else {
            // Regular selectors
            const selector = parentSelector ? `${parentSelector} ${key}` : `${containerSelector} ${key}`
            css += processNestedStyles(v, selector)
          }
        } else if (key.startsWith('--')) {
          // Handle CSS custom properties
          directStyles += `${key}: ${v};`
        } else {
          // Assume it's a selector if it contains certain characters
          const selector = parentSelector ? `${parentSelector} ${key}` : `${containerSelector} ${key}`
          css += processNestedStyles(v, selector)
        }
      } else {
        // Direct CSS properties - convert camelCase to kebab-case
        const cssProperty = camelToKebab(key)
        directStyles += processCSSValue(cssProperty, v)
      }
    }

    if (directStyles) {
      // Use the current selector context, or default to container for root styles
      const targetSelector = parentSelector || containerSelector
      if (targetSelector) {
        css = `${targetSelector} {\n  ${directStyles.replace(/; /g, ';\n  ').trim()}\n}\n` + css
      }
    }

    return css
  }

  return processNestedStyles(sx)
}

/**
 * Merge multiple sx objects with proper hierarchy
 * Later objects override earlier ones, with proper deep merging for nested selectors
 * Priority: defaults < provider < component
 */
export function mergeSx(...sxObjects: (SxProps | undefined)[]): SxProps {
  const mergeDeep = (target: any, source: any): any => {
    if (!source) return target
    if (!target) return source

    const result = { ...target }

    for (const [key, value] of Object.entries(source)) {
      if (typeof value === 'object' && value !== null && !Array.isArray(value) &&
        typeof result[key] === 'object' && result[key] !== null && !Array.isArray(result[key])) {
        // Deep merge nested objects (selectors)
        result[key] = mergeDeep(result[key], value)
      } else {
        // Override primitive values and arrays
        result[key] = value
      }
    }

    return result
  }

  return sxObjects.filter((sx): sx is SxProps => Boolean(sx)).reduce((merged, sx) => mergeDeep(merged, sx), {} as SxProps)
}

/**
 * Generate CSS custom properties from sx object for advanced styling
 * This allows for CSS selector targeting while maintaining type safety
 */
export function sxToCustomProperties(sx?: SxProps, prefix: string = '--sx'): Record<string, string> {
  if (!sx) return {}

  const customProps: Record<string, string> = {}

  const processObject = (obj: any, currentPrefix: string = prefix) => {
    for (const [key, value] of Object.entries(obj)) {
      const v = typeof value === 'function' ? value() : value
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        processObject(v, `${currentPrefix}-${key}`)
      } else {
        customProps[`${currentPrefix}-${key}`] = String(v)
      }
    }
  }

  processObject(sx)
  return customProps
}
